/**
 * Image utilities for handling image insertion and path generation
 */

/**
 * Get relative path for an image within the vault
 * This converts an absolute path to a vault-relative path
 *
 * @param imagePath - Absolute path to the image file
 * @param vaultPath - Absolute path to the vault root
 * @returns Vault-relative path (e.g., "images/photo.png" or "attachments/photo.png")
 */
export function getVaultRelativePath(imagePath: string, vaultPath: string): string {
  // Normalize paths (handle both Windows and Unix)
  const normalizedImagePath = imagePath.replace(/\\/g, '/')
  const normalizedVaultPath = vaultPath.replace(/\\/g, '/')

  // Remove vault path from image path
  if (normalizedImagePath.startsWith(normalizedVaultPath)) {
    let relativePath = normalizedImagePath.substring(normalizedVaultPath.length)
    // Remove leading slash
    relativePath = relativePath.replace(/^\/+/, '')
    return relativePath
  }

  // If not in vault, return the original path
  return imagePath
}

/**
 * Generate markdown image syntax
 *
 * @param imagePath - Path to the image (relative or absolute)
 * @param altText - Alt text for the image (optional, defaults to empty)
 * @returns Markdown image syntax: ![](path)
 */
export function generateImageMarkdown(imagePath: string, altText?: string): string {
  // Use empty alt text by default for cleaner markdown
  const alt = altText || ''

  return `![${alt}](${imagePath})`
}

/**
 * Check if a file is an image based on extension
 *
 * @param filename - Name of the file
 * @returns True if the file is an image
 */
export function isImageFile(filename: string): boolean {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico']
  const ext = filename.toLowerCase().match(/\.[^.]+$/)?.[0] || ''
  return imageExtensions.includes(ext)
}
